import { useState, useEffect, useMemo, useRef } from 'react';
import {
  TrendingUp, TrendingDown, Filter, Loader2, X, ArrowUpDown,
  AlertTriangle, Clock, Search, Gift, Trophy, Info, BarChart3, ChevronDown, ChevronRight, Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

const WINDOW_PRESETS = [14, 30, 60];
const BASELINE_PRESETS = [60, 90, 180];

const METRIC_OPTIONS = [
  { key: 'hours', label: 'Hours played', unit: 'hours' },
  { key: 'xp', label: 'XP / points', unit: 'points' },
  { key: 'avgWager', label: 'Avg wager', unit: 'currency' },
  { key: 'visitDays', label: 'Visit days', unit: 'days' },
  { key: 'sessions', label: 'Sessions', unit: 'sessions' },
];
const UNIT_BY_KEY = Object.fromEntries(METRIC_OPTIONS.map((m) => [m.key, m.unit]));
const LABEL_BY_KEY = Object.fromEntries(METRIC_OPTIONS.map((m) => [m.key, m.label]));

function fmtVal(unit, v) {
  if (v == null) return '--';
  if (unit === 'hours') return `${Number(v).toFixed(1)}h`;
  if (unit === 'currency') return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return Math.round(v).toLocaleString();
}
function fmtDelta(unit, v) {
  if (v == null) return '--';
  const sign = v >= 0 ? '+' : '-';
  const a = Math.abs(v);
  if (unit === 'hours') return `${sign}${a.toFixed(1)}h`;
  if (unit === 'currency') return `${sign}$${a.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `${sign}${Math.round(a).toLocaleString()}`;
}
const fmtPct = (v) => (v == null ? '' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(0)}%`);

function LiftCell({ unit, lift, pct }) {
  if (lift == null) return <span className="text-gray-500">--</span>;
  const up = lift >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1.5 font-semibold ${up ? 'text-emerald-400' : 'text-red-400'}`}>
      <Icon size={14} />
      {fmtDelta(unit, lift)}
      {pct != null && <span className="text-xs text-gray-400">({fmtPct(pct)})</span>}
    </span>
  );
}

function Flags({ flags }) {
  if (!flags) return null;
  return (
    <span className="inline-flex gap-1">
      {flags.confidence === 'low' && (
        <span title="Thin baseline -- low confidence" className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-900/60 text-yellow-300 border border-yellow-700">LOW</span>
      )}
      {flags.inProgress && (
        <span title="Post window not fully elapsed" className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-900/60 text-blue-300 border border-blue-700 inline-flex items-center gap-0.5"><Clock size={9} />LIVE</span>
      )}
      {flags.contaminated && (
        <span title="Another reward landed inside this window" className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-900/60 text-purple-300 border border-purple-700 inline-flex items-center gap-0.5"><AlertTriangle size={9} />OVERLAP</span>
      )}
    </span>
  );
}

/** Searchable multi-select dropdown (typeahead + checkboxes + removable chips). */
function MultiSelect({ icon: Icon, label, options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectedSet = new Set(selected);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? options.filter((o) => o.label.toLowerCase().includes(q) || (o.sublabel || '').toLowerCase().includes(q)) : options;
    return base.slice(0, 200);
  }, [options, query]);
  const selectedOptions = options.filter((o) => selectedSet.has(o.value));

  function toggle(val) {
    if (selectedSet.has(val)) onChange(selected.filter((v) => v !== val));
    else onChange([...selected, val]);
  }

  return (
    <div ref={ref} className="relative">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
        <Icon size={14} className="text-amber-500" /> {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg flex items-center justify-between text-gray-300 hover:border-gray-500 transition-colors text-sm"
      >
        <span className={selected.length ? 'text-white' : 'text-gray-400'}>
          {selected.length === 0 ? placeholder : `${selected.length} selected`}
        </span>
        <ChevronDown size={16} />
      </button>

      {open && (
        <div className="absolute z-20 top-full mt-1 w-full bg-gray-700 border border-gray-600 rounded-lg shadow-xl">
          <div className="p-2 border-b border-gray-600">
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-800 rounded">
              <Search size={13} className="text-gray-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to filter..."
                className="w-full bg-transparent text-white text-sm placeholder-gray-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.map((o) => (
              <label key={o.value} className="flex items-center gap-3 px-3 py-1.5 hover:bg-gray-600 cursor-pointer text-white text-sm">
                <input type="checkbox" checked={selectedSet.has(o.value)} onChange={() => toggle(o.value)} className="w-4 h-4 rounded accent-amber-500 shrink-0" />
                <span className="truncate">{o.label}{o.sublabel ? <span className="text-gray-400 text-xs ml-1">{o.sublabel}</span> : null}</span>
              </label>
            ))}
            {filtered.length === 0 && <p className="px-3 py-3 text-sm text-gray-400">No matches</p>}
            {options.length > 200 && !query && <p className="px-3 py-2 text-[11px] text-gray-500">Showing first 200 -- type to find more.</p>}
          </div>
        </div>
      )}

      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedOptions.map((o) => (
            <span key={o.value} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500 text-black">
              {o.label}
              <button onClick={() => toggle(o.value)} className="hover:opacity-70"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const RewardLift = () => {
  const [windowDays, setWindowDays] = useState(30);
  const [baselineDays, setBaselineDays] = useState(90);
  const [triggerDays] = useState(14);
  const [metric, setMetric] = useState('hours');
  const [includeLow, setIncludeLow] = useState(false);
  const [sortBy, setSortBy] = useState('lift');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectedRewards, setSelectedRewards] = useState([]);
  const [showRoi, setShowRoi] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const unit = UNIT_BY_KEY[metric] || 'hours';
  const isSum = metric !== 'avgWager';

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = { windowDays, baselineDays, triggerDays, sortBy, metric, minConfidence: 'all' };
      const res = await api.get('/admin/reports/reward-lift', { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load reward-lift report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, [windowDays, baselineDays, metric, sortBy]);

  const openDetail = async (row) => {
    setDetail({ loading: true });
    setDetailLoading(true);
    try {
      const params = { windowDays, baselineDays, triggerDays, metric };
      const res = await api.get(`/admin/reports/reward-lift/${row.redemptionId}`, { params });
      setDetail(res.data.detail);
    } catch (e) {
      toast.error('Failed to load drill-down');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Distinct players + rewards across all linked rows (so any can be picked, incl. low-confidence).
  const playerOptions = useMemo(() => {
    const m = new Map();
    for (const r of (data?.rows || [])) {
      if (r.unlinked || m.has(r.userId)) continue;
      m.set(r.userId, { value: r.userId, label: r.playerName, sublabel: r.email || '' });
    }
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const rewardOptions = useMemo(() => {
    const m = new Map();
    for (const r of (data?.rows || [])) {
      if (!m.has(r.rewardTitle)) m.set(r.rewardTitle, { value: r.rewardTitle, label: r.rewardTitle });
    }
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const hasFilter = selectedPlayers.length > 0 || selectedRewards.length > 0;

  const rows = useMemo(() => {
    let rs = (data?.rows || []).filter((r) => !r.unlinked);
    if (!includeLow && !hasFilter) rs = rs.filter((r) => r.flags?.confidence === 'ok');
    if (selectedPlayers.length) { const ps = new Set(selectedPlayers); rs = rs.filter((r) => ps.has(r.userId)); }
    if (selectedRewards.length) { const rw = new Set(selectedRewards); rs = rs.filter((r) => rw.has(r.rewardTitle)); }
    return rs;
  }, [data, selectedPlayers, selectedRewards, includeLow, hasFilter]);

  const totalLinked = (data?.rows || []).filter((r) => !r.unlinked).length;
  const agg = data?.aggregate || [];
  const aggRows = useMemo(() => {
    if (!selectedRewards.length) return agg;
    const rw = new Set(selectedRewards);
    return agg.filter((g) => rw.has(g.rewardTitle));
  }, [agg, selectedRewards]);

  const clearFilters = () => { setSelectedPlayers([]); setSelectedRewards([]); };

  return (
    <div className="space-y-6">
      {/* Method note */}
      <div className="flex items-start gap-2 text-sm text-gray-400 bg-gray-800/50 border border-gray-700 rounded-lg p-3">
        <Info size={16} className="text-amber-500 mt-0.5 shrink-0" />
        <span>
          Each row is one reward redemption. <span className="text-gray-300">Adjusted Lift</span> compares the player's
          {' '}<span className="text-gray-300">{LABEL_BY_KEY[metric].toLowerCase()}</span> in the {windowDays} days after the reward against their own trailing {baselineDays}-day baseline,
          excluding the {triggerDays} days just before the reward.
          {isSum ? ' Baseline is scaled to the window length.' : ' Avg wager compares the post-window mean against the baseline mean (time-weighted), not scaled.'}
          {' '}Times bucketed in Pacific.
        </span>
      </div>

      {/* Controls */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-5 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2"><BarChart3 size={14} className="text-amber-500" /> Lift metric</label>
            <select value={metric} onChange={(e) => setMetric(e.target.value)} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500">
              {METRIC_OPTIONS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2"><Clock size={14} className="text-amber-500" /> Post window</label>
            <div className="flex gap-2">
              {WINDOW_PRESETS.map((w) => (
                <button key={w} onClick={() => setWindowDays(w)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${windowDays === w ? 'bg-amber-500 text-black border-amber-500' : 'bg-gray-700 text-gray-300 border-gray-600 hover:border-gray-500'}`}>{w}d</button>
              ))}
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2"><ArrowUpDown size={14} className="text-amber-500" /> Baseline length</label>
            <div className="flex gap-2">
              {BASELINE_PRESETS.map((b) => (
                <button key={b} onClick={() => setBaselineDays(b)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${baselineDays === b ? 'bg-amber-500 text-black border-amber-500' : 'bg-gray-700 text-gray-300 border-gray-600 hover:border-gray-500'}`}>{b}d</button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <MultiSelect icon={Users} label="Players" options={playerOptions} selected={selectedPlayers} onChange={setSelectedPlayers} placeholder="All players -- type a name (e.g. Jessica)" />
          <MultiSelect icon={Gift} label="Rewards" options={rewardOptions} selected={selectedRewards} onChange={setSelectedRewards} placeholder="All rewards -- type a discount name" />
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2"><Filter size={14} className="text-amber-500" /> Sort by</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500">
              <option value="lift">Adjusted lift</option>
              <option value="liftPct">Lift %</option>
              <option value="date">Most recent</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300 mt-6 cursor-pointer">
            <input type="checkbox" checked={includeLow} onChange={(e) => setIncludeLow(e.target.checked)} className="w-4 h-4 rounded accent-amber-500" />
            Include thin-baseline / low-confidence
          </label>
          {hasFilter && (
            <button onClick={clearFilters} className="mt-6 text-xs text-amber-400 hover:text-amber-300 underline">Clear filters</button>
          )}
        </div>
      </div>

      {/* Portfolio table (primary, filterable) */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-gray-200">
            Reward events <span className="text-gray-500 font-normal">({rows.length}{hasFilter ? ' matching filters' : ''})</span>
          </h3>
          {data?.excluded && (
            <p className="text-xs text-gray-500">
              {data.excluded.lowConfidence} low-confidence {includeLow || hasFilter ? 'shown' : 'hidden'} &middot; {data.excluded.unlinked} with no synced play (hidden)
            </p>
          )}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="animate-spin mr-2" size={20} /> Building report...</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            {hasFilter ? 'No reward events match the selected players / rewards.' : 'No reward events match the current filters.'}
            {!includeLow && !hasFilter && totalLinked > 0 && <div className="text-xs text-gray-500 mt-2">Tip: most events are low-confidence (thin baseline). Tick "Include thin-baseline" or pick a player to see them.</div>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700 bg-gray-800">
                  <th className="py-3 px-4 font-medium">Player</th>
                  <th className="py-3 px-3 font-medium">Reward</th>
                  <th className="py-3 px-3 font-medium">Redeemed</th>
                  <th className="py-3 px-3 font-medium text-right">Baseline</th>
                  <th className="py-3 px-3 font-medium text-right">Post</th>
                  <th className="py-3 px-3 font-medium text-right">Adjusted lift ({LABEL_BY_KEY[metric].toLowerCase()})</th>
                  <th className="py-3 px-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.redemptionId} onClick={() => openDetail(r)} className="border-b border-gray-700/50 hover:bg-gray-700/40 cursor-pointer">
                    <td className="py-3 px-4">
                      <div className="text-gray-100 font-medium">{r.playerName}</div>
                      <div className="text-xs text-gray-500">{r.email}</div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="text-gray-200">{r.rewardTitle}</div>
                      {r.faceValue != null && <div className="text-xs text-gray-500">${r.faceValue.toLocaleString()}{r.faceValueSource === 'title' ? ' (from name)' : ''}</div>}
                    </td>
                    <td className="py-3 px-3 text-gray-300">{r.anchorDay}</td>
                    <td className="py-3 px-3 text-right text-gray-300">{fmtVal(unit, r.headline?.baselinePerWindow)}</td>
                    <td className="py-3 px-3 text-right text-gray-300">{fmtVal(unit, r.headline?.post)}</td>
                    <td className="py-3 px-3 text-right"><LiftCell unit={unit} lift={r.headline?.lift} pct={r.headline?.liftPct} /></td>
                    <td className="py-3 px-3"><Flags flags={r.flags} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ROI roll-up (secondary, collapsible + capped) */}
      {agg.length > 0 && (
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <button onClick={() => setShowRoi((v) => !v)} className="w-full flex items-center justify-between px-5 py-3 text-left">
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-200"><Trophy size={15} className="text-amber-500" /> Reward ROI (grouped by reward, median {LABEL_BY_KEY[metric].toLowerCase()} lift)</span>
            <span className="flex items-center gap-2 text-xs text-gray-500">{aggRows.length} reward{aggRows.length === 1 ? '' : 's'}{showRoi ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
          </button>
          {showRoi && (
            <div className="px-5 pb-5">
              <div className="overflow-x-auto max-h-80 overflow-y-auto border border-gray-700/50 rounded">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-800">
                    <tr className="text-left text-gray-400 border-b border-gray-700">
                      <th className="py-2 px-3 font-medium">Reward</th>
                      <th className="py-2 px-3 font-medium text-right">Events</th>
                      <th className="py-2 px-3 font-medium text-right">Median lift</th>
                      <th className="py-2 px-3 font-medium text-right">% positive</th>
                      {isSum && <th className="py-2 px-3 font-medium text-right">Total incr.</th>}
                      <th className="py-2 px-3 font-medium text-right">Est. cost</th>
                      {isSum && <th className="py-2 px-3 font-medium text-right">Incr / $</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {aggRows.map((g) => (
                      <tr key={g.rewardId} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="py-2 px-3 text-gray-200">{g.rewardTitle}</td>
                        <td className="py-2 px-3 text-right text-gray-300">{g.usableEvents}<span className="text-gray-500">/{g.events}</span></td>
                        <td className="py-2 px-3 text-right"><LiftCell unit={unit} lift={g.medianLift} /></td>
                        <td className="py-2 px-3 text-right text-gray-300">{g.pctPositive == null ? '--' : `${(g.pctPositive * 100).toFixed(0)}%`}</td>
                        {isSum && <td className="py-2 px-3 text-right text-gray-300">{fmtDelta(unit, g.totalIncremental)}</td>}
                        <td className="py-2 px-3 text-right text-gray-300">{g.estTotalCost == null ? '--' : `$${g.estTotalCost.toLocaleString()}`}</td>
                        {isSum && <td className="py-2 px-3 text-right text-gray-300">{g.incrementalPerDollar == null ? '--' : g.incrementalPerDollar.toFixed(3)}</td>}
                      </tr>
                    ))}
                    {aggRows.length === 0 && (
                      <tr><td colSpan={7} className="py-6 text-center text-gray-500">No rewards match the selection.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {detail && (
        <DetailModal detail={detail} loading={detailLoading} metric={metric} onClose={() => setDetail(null)} />
      )}
    </div>
  );
};

function DetailModal({ detail, loading, metric, onClose }) {
  const metricRows = [
    { key: 'hours', label: 'Hours played', unit: 'hours' },
    { key: 'avgWager', label: 'Avg wager', unit: 'currency' },
    { key: 'visitDays', label: 'Visit days', unit: 'days' },
    { key: 'sessions', label: 'Sessions', unit: 'sessions' },
    { key: 'xp', label: 'XP / points', unit: 'points' },
  ];
  const headlineUnit = UNIT_BY_KEY[metric] || 'hours';
  const maxWeekly = useMemo(() => {
    if (!detail?.weekly?.length) return 1;
    return Math.max(1, ...detail.weekly.map((w) => Math.abs(w.value || 0)));
  }, [detail]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {loading || detail.loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="animate-spin mr-2" size={20} /> Loading...</div>
        ) : (
          <>
            <div className="flex items-start justify-between p-5 border-b border-gray-700">
              <div>
                <div className="flex items-center gap-2"><Gift size={18} className="text-amber-500" /><h2 className="text-lg font-semibold text-white">{detail.playerName}</h2></div>
                <p className="text-sm text-gray-400 mt-1">{detail.rewardTitle} &middot; redeemed {detail.anchorDay}{detail.redeemedByStaffName ? ` by ${detail.redeemedByStaffName}` : ''}</p>
                <div className="mt-2"><Flags flags={detail.flags} /></div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>

            <div className="p-5 space-y-6">
              <div className="bg-gray-800 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide">Adjusted lift &middot; {LABEL_BY_KEY[metric]} &middot; {detail.windowDays}d window</div>
                  <div className="mt-1 text-2xl"><LiftCell unit={headlineUnit} lift={detail.headline?.lift} pct={detail.headline?.liftPct} /></div>
                </div>
                {detail.flags?.inProgress && (
                  <div className="text-right text-xs text-blue-300">
                    <div>{detail.elapsedDays} of {detail.windowDays} days elapsed</div>
                    <div className="text-gray-400">Projected: {fmtDelta(headlineUnit, detail.headline?.projectedLift)}</div>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-700">
                      <th className="py-2 pr-4 font-medium">Metric</th>
                      <th className="py-2 px-3 font-medium text-right">Baseline</th>
                      <th className="py-2 px-3 font-medium text-right">Post</th>
                      <th className="py-2 pl-3 font-medium text-right">Lift</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metricRows.map((m) => {
                      const d = detail.metrics?.[m.key];
                      if (!d) return null;
                      const highlight = m.key === metric;
                      return (
                        <tr key={m.key} className={`border-b border-gray-700/50 ${highlight ? 'bg-amber-500/5' : ''}`}>
                          <td className="py-2 pr-4 text-gray-200">{m.label}{highlight ? <span className="ml-2 text-[10px] text-amber-400">selected</span> : null}</td>
                          <td className="py-2 px-3 text-right text-gray-300">{fmtVal(m.unit, d.baselinePerWindow)}</td>
                          <td className="py-2 px-3 text-right text-gray-300">{fmtVal(m.unit, d.post)}</td>
                          <td className="py-2 pl-3 text-right"><LiftCell unit={m.unit} lift={d.lift} pct={d.liftPct} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {detail.weekly?.length > 0 && (
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Weekly {LABEL_BY_KEY[metric].toLowerCase()} (baseline &rarr; reward &rarr; post)</div>
                  <div className="flex items-end gap-1 h-28 border-b border-gray-700 pb-0">
                    {detail.weekly.map((w, i) => {
                      const h = Math.round((Math.abs(w.value || 0) / maxWeekly) * 100);
                      const color = w.isTriggerWindow ? 'bg-yellow-600' : w.isPost ? 'bg-emerald-500' : 'bg-gray-600';
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${fmtVal(headlineUnit, w.value)} (${w.weekStartOffset >= 0 ? '+' : ''}${w.weekStartOffset}d)`}>
                          <div className={`w-full rounded-t ${color}`} style={{ height: `${Math.max(2, h)}%` }} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-[11px] text-gray-500">
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-600 inline-block" /> baseline</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-600 inline-block" /> trigger (excluded)</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> post-reward</span>
                  </div>
                </div>
              )}

              {detail.unlinked && (
                <div className="text-sm text-yellow-300 bg-yellow-900/30 border border-yellow-700 rounded-lg p-3">
                  This player has no synced play activity, so lift can't be computed.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default RewardLift;
